import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { adminGetCompanyAppointmentStats, adminGetCompanyByUserId } from '../../../api/appUsersApi';
import { getResourceCounts } from '../../../../packages/utils/packageFeatures';
import './styles/UserUsageTracking.css';

const UserUsageTracking = ({ user }) => {
  const [loadingCompany, setLoadingCompany] = useState(true);
  const [loadingStats, setLoadingStats] = useState(false);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [monthlyStats, setMonthlyStats] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchCompanyDetails = async () => {
      try {
        if (!user?.id) {
          console.error('No user ID provided');
          if (isMounted) {
            setError('User information not available');
          }
          return;
        }

        console.log('Fetching company details for user:', user.id);
        setLoadingCompany(true);
        const result = await adminGetCompanyByUserId(user.id);
        console.log('Company details response:', result);
        
        if (isMounted) {
          if (result.exists) {
            setCompany(result.company);
          } else {
            console.log('No company found for user:', user.id);
            setError('No company found for this user');
          }
        }
      } catch (err) {
        console.error('Error fetching company details:', err);
        if (isMounted) {
          // Check if the request failed
          if (err.code === 'functions/internal') {
            setError('Internal server error. Please try again later.');
          } else {
            setError(`Failed to load company details: ${err.message}`);
          }
        }
      } finally {
        if (isMounted) {
          setLoadingCompany(false);
        }
      }
    };

    fetchCompanyDetails();

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  useEffect(() => {
    let isMounted = true;

    const fetchMonthlyStats = async () => {
      if (!company) return;
      
      try {
        setLoadingStats(true);
        setError(null);
        const [year, month] = selectedMonth.split('-');
        
        // Fetch appointment stats only (SMS tracking removed)
        const stats = await adminGetCompanyAppointmentStats(company.id, { year, month });
        
        // Process the appointment stats
        const monthStats = {
          month: format(new Date(year, parseInt(month) - 1), 'MMMM yyyy'),
          totalAppointments: stats.totalAppointments || 0,
          activeAppointments: stats.appointments?.filter(apt => !apt.status || apt.status === 'pending' || apt.status === 'confirmed').length || 0,
          completedAppointments: stats.statusCounts?.completed || 0,
          cancelledAppointments: stats.statusCounts?.cancelled || 0
        };

        if (isMounted) {
          setMonthlyStats(monthStats);
        }
      } catch (err) {
        console.error('Error fetching monthly stats:', err);
        if (isMounted) {
          setError('Failed to load usage statistics');
        }
      } finally {
        if (isMounted) {
          setLoadingStats(false);
        }
      }
    };

    fetchMonthlyStats();

    return () => {
      isMounted = false;
    };
  }, [company, selectedMonth]);

  // Get available months (up to 12 months in the past)
  const getAvailableMonths = () => {
    const months = [];
    const now = new Date();
    
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        value: format(date, 'yyyy-MM'),
        label: format(date, 'MMMM yyyy')
      });
    }
    return months;
  };

  if (!company && loadingCompany) {
    return (
      <div className="admin-card user-section-card userUsageCard" aria-busy="true">
        <div className="uuHeader">
          <div>
            <h3 className="uuTitle">Usage Tracking</h3>
            <p className="uuSub">
              <span className="uuSkeletonBar" style={{ width: 180, height: 14, display: 'inline-block' }} />
            </p>
          </div>
          <div className="uuSelectWrap">
            <label>Month</label>
            <div className="uuSkeletonBar" style={{ width: 220, height: 40, borderRadius: 12 }} />
          </div>
        </div>

        <div className="uuGrid">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div className="uuStat" key={idx}>
              <div className="uuSkeletonBar" style={{ width: 140 }} />
              <div className="uuSkeletonBar" style={{ width: 70, height: 30, marginTop: 10 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="admin-card user-section-card userUsageCard">
        <div className="uuAlert uuAlertError">No company found for this user.</div>
      </div>
    );
  }

  return (
    <div className="admin-card user-section-card userUsageCard" aria-busy={loadingStats ? 'true' : 'false'}>
      <div className="uuHeader">
        <div>
          <h3 className="uuTitle">Usage Tracking</h3>
          <p className="uuSub">{company.name}</p>
        </div>
        <div className="uuSelectWrap">
          <label htmlFor="uuMonth">Month</label>
          <select
            id="uuMonth"
            className="uuSelect"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            disabled={loadingStats}
          >
            {getAvailableMonths().map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error ? (
        <div className="uuAlert uuAlertError" role="alert" aria-live="polite">
          {error}
        </div>
      ) : null}

      <div className="uuPillsRow">
        <span className="uuPill uuPillBlue">Current package: {company.package?.name || 'No package'}</span>
        <span className="uuPill uuPillGreen">
          Appointment limit:{' '}
          {company.package?.limits?.appointments === -1
            ? 'Unlimited'
            : `${getResourceCounts(company).appointments} tracked (plan limit not enforced)`}
        </span>
      </div>

      <div className="uuGrid" aria-label="Appointment usage stats">
        {loadingStats || !monthlyStats ? (
          Array.from({ length: 4 }).map((_, idx) => (
            <div className="uuStat" key={idx}>
              <div className="uuSkeletonBar" style={{ width: 160 }} />
              <div className="uuSkeletonBar" style={{ width: 70, height: 30, marginTop: 10 }} />
            </div>
          ))
        ) : (
          <>
            <div className="uuStat uuStat--total">
              <div className="uuStatLabel">
                Total appointments
              </div>
              <div className="uuStatValue">{monthlyStats.totalAppointments}</div>
            </div>
            <div className="uuStat uuStat--active">
              <div className="uuStatLabel">
                Active
              </div>
              <div className="uuStatValue">{monthlyStats.activeAppointments}</div>
            </div>
            <div className="uuStat uuStat--completed">
              <div className="uuStatLabel">
                Completed
              </div>
              <div className="uuStatValue">{monthlyStats.completedAppointments}</div>
            </div>
            <div className="uuStat uuStat--cancelled">
              <div className="uuStatLabel">
                Cancelled
              </div>
              <div className="uuStatValue">{monthlyStats.cancelledAppointments}</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UserUsageTracking; 